import { useState } from "react";

interface QuerySource {
  node_id: string;
  title: string;
  excerpt: string;
  confidence: number;
  source_type: string;
}

interface QueryResult {
  answer: string;
  sources: QuerySource[];
  follow_ups: string[];
}

interface QueryPanelProps {
  kbId: string;
}

export function QueryPanel({ kbId }: QueryPanelProps) {
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState<QueryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitQuery(nextQuestion?: string) {
    const prompt = (nextQuestion || question).trim();
    if (!prompt) {
      return;
    }
    setLoading(true);
    setError(null);
    if (nextQuestion) {
      setQuestion(nextQuestion);
    }
    try {
      const response = await fetch(`/api/knowledge/${kbId}/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: prompt }),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || "Query failed");
      }
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Query failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={S.root}>
      <div style={S.promptCard}>
        <div style={S.heading}>RAG Query</div>
        <div style={S.subheading}>
          Ask questions against the indexed nodes in this knowledge base.
        </div>
        <textarea
          style={S.textarea}
          rows={5}
          placeholder="What decisions shaped the current AWS deployment path?"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
        />
        <div style={S.toolbar}>
          <button
            style={{ ...S.primaryBtn, opacity: !question.trim() || loading ? 0.45 : 1 }}
            disabled={!question.trim() || loading}
            onClick={() => submitQuery()}
          >
            {loading ? "Querying..." : "Run Query"}
          </button>
          {error && <span style={S.error}>{error}</span>}
        </div>
      </div>

      <div style={S.resultGrid}>
        <div style={S.answerCard}>
          <div style={S.sectionLabel}>Answer</div>
          <div style={S.answerText}>
            {result?.answer || "No answer yet. Run a query to see an answer and supporting evidence."}
          </div>
        </div>

        <div style={S.sideColumn}>
          <div style={S.sideCard}>
            <div style={S.sectionLabel}>Supporting Sources</div>
            {result?.sources?.length ? (
              result.sources.map((source) => (
                <div key={source.node_id} style={S.sourceCard}>
                  <div style={S.sourceTop}>
                    <span style={S.sourceTitle}>{source.title}</span>
                    <span style={S.sourceConfidence}>{Math.round(source.confidence * 100)}%</span>
                  </div>
                  <div style={S.sourceMeta}>{source.source_type}</div>
                  <div style={S.sourceExcerpt}>{source.excerpt}</div>
                </div>
              ))
            ) : (
              <div style={S.empty}>No supporting sources yet.</div>
            )}
          </div>

          <div style={S.sideCard}>
            <div style={S.sectionLabel}>Follow-ups</div>
            {result?.follow_ups?.length ? (
              result.follow_ups.map((followUp) => (
                <button key={followUp} style={S.followUpBtn} onClick={() => submitQuery(followUp)}>
                  {followUp}
                </button>
              ))
            ) : (
              <div style={S.empty}>Follow-up prompts will appear after the first query.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    padding: "1rem",
  },
  promptCard: {
    padding: "1rem",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
  },
  heading: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: "var(--ot-text-primary, #e6edf3)",
  },
  subheading: {
    marginTop: "0.25rem",
    fontSize: "0.72rem",
    color: "var(--ot-text-secondary, #8b949e)",
  },
  textarea: {
    width: "100%",
    marginTop: "0.8rem",
    resize: "vertical",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "8px",
    color: "var(--ot-text-primary, #e6edf3)",
    padding: "0.75rem 0.8rem",
    fontSize: "0.78rem",
    lineHeight: 1.5,
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
    boxSizing: "border-box",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginTop: "0.8rem",
  },
  primaryBtn: {
    padding: "0.55rem 0.8rem",
    borderRadius: "6px",
    border: "none",
    background: "var(--ot-accent, #80f3b2)",
    color: "var(--ot-bg-deep, #050608)",
    cursor: "pointer",
    fontSize: "0.75rem",
    fontWeight: 700,
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  error: {
    color: "#ff7b72",
    fontSize: "0.72rem",
  },
  resultGrid: {
    display: "grid",
    gridTemplateColumns: "1.25fr 0.9fr",
    gap: "1rem",
  },
  answerCard: {
    minHeight: "320px",
    padding: "1rem",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
  },
  sideColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  sideCard: {
    padding: "1rem",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
  },
  sectionLabel: {
    marginBottom: "0.6rem",
    fontSize: "0.68rem",
    color: "var(--ot-accent, #80f3b2)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  answerText: {
    whiteSpace: "pre-wrap",
    lineHeight: 1.7,
    color: "var(--ot-text-primary, #e6edf3)",
    fontSize: "0.78rem",
  },
  sourceCard: {
    padding: "0.75rem",
    borderRadius: "8px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.05)",
    marginTop: "0.6rem",
  },
  sourceTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: "0.5rem",
  },
  sourceTitle: {
    color: "var(--ot-text-primary, #e6edf3)",
    fontSize: "0.76rem",
    fontWeight: 600,
  },
  sourceConfidence: {
    color: "var(--ot-cyan, #7ae8e8)",
    fontSize: "0.68rem",
  },
  sourceMeta: {
    marginTop: "0.2rem",
    fontSize: "0.66rem",
    color: "var(--ot-text-muted, #6e7681)",
  },
  sourceExcerpt: {
    marginTop: "0.45rem",
    fontSize: "0.72rem",
    lineHeight: 1.5,
    color: "var(--ot-text-secondary, #8b949e)",
  },
  followUpBtn: {
    width: "100%",
    textAlign: "left",
    marginTop: "0.5rem",
    padding: "0.7rem 0.8rem",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.03)",
    color: "var(--ot-text-primary, #e6edf3)",
    cursor: "pointer",
    fontSize: "0.73rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  empty: {
    color: "var(--ot-text-muted, #6e7681)",
    fontSize: "0.72rem",
    lineHeight: 1.6,
  },
};
